import { EmbedParams } from '@act/shared/data-transfer-objects';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { sigmaEmbedRequests } from 'redux/requests/sigma-embed.requests';
import { useApiCall } from 'redux/utils/api-hook';
import styled from 'styled-components';
import Spinner from 'toolkit/progress/spinner';

interface SigmaEmbedProps {
  embedKey: string;
  embedParams?: EmbedParams;
  className?: string;
  style?: CSSProperties;
}

const LoaderContainer = styled.div`
  height: 100px;
`;

const SigmaEmbed = (props: SigmaEmbedProps) => {
  const [response, isFetchingEmbedLink, getEmbededSigmaLink] = useApiCall(
    sigmaEmbedRequests.getEmbededSigmaRequest
  );

  // Load Sigma Link on mount
  useEffect(() => {
    setIsSigmaLoaded(false);
    getEmbededSigmaLink({
      params: null,
      body: { key: props.embedKey, params: props.embedParams },
    });
  }, [props.embedKey, props.embedParams]);

  // Set a timer to fetch a fresh sigma embed link after the one we just got has expired.
  useEffect(() => {
    // We only need to start the timer once "isFetchingEmbedLink" is "false" which means it the response must have just came in because this useEffect should only run when "isFetchingEmbedLink" has changed
    if (isFetchingEmbedLink === false && response?.expirationTimestamp) {
      const reloadCountdown =
        response.expirationTimestamp - new Date().getTime();
      if (reloadCountdown > 0) {
        const timeout = setTimeout(
          () =>
            getEmbededSigmaLink({
              params: null,
              body: { key: props.embedKey, params: props.embedParams },
            }),
          reloadCountdown
        );
        return () => clearTimeout(timeout); // Clean up timeout when component is destryed
      }
    }
  }, [isFetchingEmbedLink, response]);

  const [height, setHeight] = useState(0);
  const [isSigmaLoaded, setIsSigmaLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Set the height when sigma height change event is fired
  // Listen to Sigma Event for "workbook:pageheight:onchange" (https://help.sigmacomputing.com/hc/en-us/articles/6797945342483-User-Backed-Embedding)
  useEffect(() => {
    function updateHeight(event) {
      const isFromThisEmbed =
        event.source === iframeRef?.current?.contentWindow;
      if (!isFromThisEmbed) {
        return;
      }

      if (event.data.type === 'workbook:pageheight:onchange') {
        setHeight(Math.ceil(event.data.pageHeight));
      }

      if (event.data.type === 'workbook:loaded') {
        setIsSigmaLoaded(true);
      }
    }

    // Listen to event
    window.addEventListener('message', updateHeight, false);

    // Stop Listening on cleanup
    return () => window.removeEventListener('message', updateHeight, false);
  });

  if (!isFetchingEmbedLink && !response?.hasEmbed) return;

  return (
    <>
      {(isFetchingEmbedLink || !isSigmaLoaded) && (
        <LoaderContainer>
          <Spinner centered={true} />
        </LoaderContainer>
      )}
      {response && (
        <iframe
          title="Sigma Embed"
          ref={iframeRef}
          className={props.className}
          style={{
            display: 'block',
            border: 'none',
            width: '100%',
            height: `${isSigmaLoaded ? height : 0}px`,
            ...props.style,
          }}
          src={response.url}
        />
      )}
    </>
  );
};

export default SigmaEmbed;
